import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { appConfirmMessage } from '../../utils/appConfirm';
import { FaPencilAlt, FaTrash } from 'react-icons/fa';
import { graphqlRequest } from '../../utils/graphqlRequest';
import {
  CREATE_SUPPORT_FAQ_MUTATION,
  DELETE_SUPPORT_FAQ_MUTATION,
  SUPPORT_FAQS_QUERY,
  UPDATE_SUPPORT_FAQ_MUTATION,
} from '../../graphql/supportFaq';

const CATEGORIES = [
  'All Categories',
  'Time Tracking',
  'Payments',
  'Reports',
  'Account Settings',
  'Jobs & Assignments',
];

function mapApiRow(r) {
  const created = r.createdAt ? String(r.createdAt).slice(0, 10) : '';
  const st = String(r.status || '').toLowerCase();
  return {
    id: r.id,
    question: r.question,
    answer: r.answer ?? '',
    category: r.category,
    keywords: r.keywords ?? '',
    createdBy: '—',
    lastUpdated: created,
    status: st === 'draft' ? 'Draft' : 'Published',
  };
}

export default function SupportFaqManagement() {
  const [category, setCategory] = useState('All Categories');
  const [search, setSearch] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [addFaqOpen, setAddFaqOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [faqError, setFaqError] = useState('');

  const FORM_CATEGORIES = CATEGORIES.filter((c) => c !== 'All Categories');

  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    category: FORM_CATEGORIES[0] ?? 'Time Tracking',
    keywords: '',
    visibility: 'Published',
  });

  const rows = useMemo(() => {
    return faqs.filter((f) => {
      if (category !== 'All Categories' && f.category !== category) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!`${f.question} ${f.category}`.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [category, search, faqs]);

  const loadFaqs = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      const data = await graphqlRequest(SUPPORT_FAQS_QUERY);
      const list = data?.supportFaqs || [];
      setFaqs(list.map(mapApiRow));
    } catch (err) {
      setListError(err?.message || 'Failed to load FAQs');
      setFaqs([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFaqs();
  }, [loadFaqs]);

  const closeFaqModal = useCallback(() => {
    setAddFaqOpen(false);
    setEditingId(null);
    setFaqError('');
  }, []);

  useEffect(() => {
    if (!addFaqOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeFaqModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [addFaqOpen, closeFaqModal]);

  const openAddFaq = () => {
    setFaqError('');
    setEditingId(null);
    setFaqForm((prev) => ({
      ...prev,
      question: '',
      answer: '',
      keywords: '',
      visibility: 'Published',
      category: FORM_CATEGORIES[0] ?? prev.category,
    }));
    setAddFaqOpen(true);
  };

  const openEditFaq = (row) => {
    setFaqError('');
    setEditingId(row.id);
    const cat = row.category;
    setFaqForm({
      question: row.question ?? '',
      answer: row.answer ?? '',
      category: FORM_CATEGORIES.includes(cat) ? cat : FORM_CATEGORIES[0] ?? 'Time Tracking',
      keywords: row.keywords ?? '',
      visibility: row.status === 'Draft' ? 'Draft' : 'Published',
    });
    setAddFaqOpen(true);
  };

  const handleDeleteFaq = async (row) => {
    const ok = await appConfirmMessage(
      `Delete this FAQ?\n\n"${String(row.question).slice(0, 80)}${String(row.question).length > 80 ? '…' : ''}"`,
    );
    if (!ok) return;
    setDeleteLoadingId(row.id);
    try {
      await graphqlRequest(DELETE_SUPPORT_FAQ_MUTATION, { id: row.id });
      await loadFaqs();
    } catch (err) {
      toast.error(err?.message || 'Could not delete FAQ.');
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const handleSaveFaq = async (e) => {
    e.preventDefault();
    setFaqError('');

    const question = String(faqForm.question ?? '').trim();
    const answer = String(faqForm.answer ?? '').trim();
    const selectedCategory = String(faqForm.category ?? '').trim();
    const keywords = String(faqForm.keywords ?? '').trim();

    if (!question) return setFaqError('Question is required.');
    if (!answer) return setFaqError('Answer is required.');
    if (!selectedCategory) return setFaqError('Category is required.');

    setSaveLoading(true);
    try {
      const status = faqForm.visibility === 'Draft' ? 'DRAFT' : 'PUBLISHED';
      if (editingId) {
        await graphqlRequest(UPDATE_SUPPORT_FAQ_MUTATION, {
          input: {
            id: editingId,
            question,
            answer,
            category: selectedCategory,
            keywords: keywords || null,
            status,
          },
        });
      } else {
        const input = {
          question,
          answer,
          category: selectedCategory,
          ...(keywords ? { keywords } : {}),
          ...(faqForm.visibility === 'Draft' ? { status: 'DRAFT' } : { status: 'PUBLISHED' }),
        };
        await graphqlRequest(CREATE_SUPPORT_FAQ_MUTATION, { input });
      }
      await loadFaqs();
      closeFaqModal();
    } catch (err) {
      setFaqError(err?.message || 'Could not save FAQ. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="support-faq-page">
      <div
        className="smgmt-page-head"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h1>FAQ Management</h1>
          <p>Manage the FAQs shown in Help &amp; Support and the FAQ page</p>
          {listError ? (
            <p className="smgmt-muted" style={{ color: '#b91c1c', marginTop: 8 }}>
              {listError}
            </p>
          ) : null}
        </div>
        <button type="button" className="smgmt-btn smgmt-btn-primary" onClick={openAddFaq}>
          + Add New FAQ
        </button>
      </div>

      <div className="smgmt-card">
        <p className="smgmt-card-title" style={{ marginTop: 0 }}>
          Filter by category
        </p>
        <div className="smgmt-pills">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              className={`smgmt-pill${category === c ? ' active' : ''}`}
              onClick={() => setCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>
        <label style={{ display: 'block', marginTop: 8 }}>
          <span className="smgmt-muted" style={{ display: 'block', marginBottom: 6 }}>
            Search FAQs
          </span>
          <input
            type="search"
            className="smgmt-search"
            placeholder="Search questions or answers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 400 }}
          />
        </label>
      </div>

      <div className="smgmt-card">
        <div className="smgmt-table-toolbar">
          <div>
            <h2>All FAQs</h2>
            <p className="smgmt-muted">
              {listLoading
                ? 'Loading…'
                : `Showing ${rows.length} of ${faqs.length} FAQs`}
            </p>
          </div>
        </div>
        <div className="smgmt-table-wrap">
          <table className="smgmt-table">
            <thead>
              <tr>
                <th>Question</th>
                <th>Category</th>
                <th>Created by</th>
                <th>Last updated</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.id}>
                  <td>{f.question}</td>
                  <td>
                    <span className="smgmt-badge smgmt-badge--medium">{f.category}</span>
                  </td>
                  <td>{f.createdBy}</td>
                  <td>{f.lastUpdated}</td>
                  <td>
                    <span className="smgmt-badge smgmt-badge--resolved">{f.status}</span>
                  </td>
                  <td>
                    <div className="smgmt-icon-actions">
                      <button
                        type="button"
                        aria-label="Edit"
                        onClick={() => openEditFaq(f)}
                        disabled={Boolean(deleteLoadingId)}
                      >
                        <FaPencilAlt color="#7c3aed" />
                      </button>
                      <button
                        type="button"
                        className="danger"
                        aria-label="Delete"
                        onClick={() => handleDeleteFaq(f)}
                        disabled={deleteLoadingId === f.id}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {addFaqOpen && (
        <div
          className="smgmt-faq-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={editingId ? 'Edit FAQ' : 'Add New FAQ'}
          onClick={closeFaqModal}
        >
          <div className="smgmt-faq-modal" onClick={(e) => e.stopPropagation()}>
            <div className="smgmt-faq-modal-header">
              <div>
                <h3 className="smgmt-faq-modal-title">{editingId ? 'Edit FAQ' : 'Add New FAQ'}</h3>
                <p className="smgmt-faq-modal-subtitle">
                  {editingId
                    ? 'Update this FAQ. Changes apply to Help & Support and the FAQ page when published.'
                    : 'Create a new FAQ. Changes apply to Help & Support and the FAQ page when published.'}
                </p>
              </div>
              <button
                type="button"
                className="smgmt-faq-modal-close"
                aria-label="Close"
                onClick={closeFaqModal}
              >
                ×
              </button>
            </div>

            <div className="smgmt-faq-modal-body">
              <form className="smgmt-faq-modal-form" onSubmit={handleSaveFaq}>
                <label className="smgmt-form-field">
                  <span className="smgmt-form-label">
                    Question <span className="smgmt-required">*</span>
                  </span>
                  <input
                    type="text"
                    className="smgmt-faq-input"
                    placeholder="Enter the frequently asked question"
                    value={faqForm.question}
                    onChange={(e) => setFaqForm((p) => ({ ...p, question: e.target.value }))}
                  />
                </label>

                <label className="smgmt-form-field">
                  <span className="smgmt-form-label">
                    Answer <span className="smgmt-required">*</span>
                  </span>
                  <textarea
                    className="smgmt-faq-textarea"
                    placeholder="Provide a detailed answer to the question"
                    value={faqForm.answer}
                    onChange={(e) => setFaqForm((p) => ({ ...p, answer: e.target.value }))}
                  />
                </label>

                <label className="smgmt-form-field">
                  <span className="smgmt-form-label">
                    Category <span className="smgmt-required">*</span>
                  </span>
                  <select
                    className="smgmt-faq-input"
                    value={faqForm.category}
                    onChange={(e) => setFaqForm((p) => ({ ...p, category: e.target.value }))}
                  >
                    {FORM_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="smgmt-form-field">
                  <span className="smgmt-form-label">Keywords</span>
                  <input
                    type="text"
                    className="smgmt-faq-input"
                    placeholder="Enter keywords separated by commas (e.g., clock in, time tracking, shift)"
                    value={faqForm.keywords}
                    onChange={(e) => setFaqForm((p) => ({ ...p, keywords: e.target.value }))}
                  />
                </label>

                <label className="smgmt-form-field">
                  <span className="smgmt-form-label">Visibility</span>
                  <select
                    className="smgmt-faq-input"
                    value={faqForm.visibility}
                    onChange={(e) => setFaqForm((p) => ({ ...p, visibility: e.target.value }))}
                  >
                    <option value="Published">Published</option>
                    <option value="Draft">Draft</option>
                  </select>
                </label>

                {faqError ? <div className="smgmt-faq-error">{faqError}</div> : null}

                <div className="smgmt-faq-modal-actions">
                  <button
                    type="submit"
                    className="smgmt-btn smgmt-btn-primary"
                    disabled={saveLoading}
                  >
                    {saveLoading ? 'Saving…' : editingId ? 'Update FAQ' : 'Save FAQ'}
                  </button>
                  <button type="button" className="smgmt-btn" onClick={closeFaqModal}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
